import Box from '@mui/material/Box';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
import AlertMessages from 'common/Alert/AlertMessages';
import { useTranslation } from 'react-i18next';
import { checkPrivileges } from 'Helpers/CheckPrivileges';
import Loader from 'layout/Loader';
import BackendPaginationTable from 'common/Table/TableWithPagination';
import { useNavigate, useParams } from 'react-router-dom';
import { Grid, Typography } from '@mui/material';
import StyledInput from 'common/SearchField/Search';
import { useGetInspectionSectionService } from 'subModule/src/services/inspectionsection/useInspectionSection';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { ChangeStatusInspectionSectionResponse, InspectionSectionResponse } from 'subModule/src/services/inspectionsection/interface';
import FormDialog from 'common/Modal/Modal';
import { InspectionFormProfileService, InspectionSectionProfileService } from 'subModule/src/core/services';
import { DeleteData } from 'subModule/src/type';
import ComponentHeaderTwo from 'common/ComponentHeaderTwo/ComponentHeaderTwo';
import { ReactComponent as Inspection  } from "assets/SideBarIcons/inspection.svg";
import InspectionFormTabs from './InspectionFormTabs';
// import ComponentSubheader from 'common/ComponentHeaderTwo/ComponentSubheader';
import type { BreadcrumbItem } from 'Helpers/generalBreadcrumbs';

export default function InspectionSectionListing() {
    const navigate = useNavigate();
    const params = useParams();
    const formId = params?.id;
    const [pageNo, setPageNo] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(20);
    const [loading, setLoading] = useState<boolean>(false);
    const [searchTerm, setSearchTerm] = useState<any>('');
    const [search, setSearch] = useState('');
    const [openDeleteModal, setDeleteModal] = useState(false);
    const [row, setRow] = useState<InspectionSectionResponse>();
    const [statusModal, setStatusModal] = useState(false);
    const [formTitle, setFormTitle] = useState('');
    const { data: tableData, isFetching, refetch } = searchTerm ? useGetInspectionSectionService({ pageNumber: pageNo, size: pageSize, title: search }, +(params as any)?.id) : useGetInspectionSectionService({ pageNumber: pageNo, size: pageSize }, +(params as any)?.id);
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation();

    useEffect(() => {
        if (!formId) return;
        let active = true;
        InspectionFormProfileService.getInspectionFormById(+formId)
            .then((res: any) => {
                if (!active) return;
                const name = String(
                    res?.data?.title ||
                        res?.data?.secondaryTitle ||
                        res?.title ||
                        '',
                ).trim();
                if (name) setFormTitle(name);
            })
            .catch(() => {});
        return () => {
            active = false;
        };
    }, [formId]);

    const breadcrumbItems: BreadcrumbItem[] = useMemo(
        () => [
            { labelKey: 'general' },
            { labelKey: 'inspectionForms', href: '/inspection-forms' },
            {
                labelKey: 'title',
                ...(formTitle ? { label: formTitle } : {}),
                href: formId ? `/inspection-form/${formId}` : undefined,
            },
            { labelKey: 'sections' },
        ],
        [formId, formTitle],
    );

    const fetchData = useCallback(() => {
        refetch().then((res: any) => {
            if (!res?.data?.pages[0]?.success) enqueueSnackbar(<AlertMessages text={res?.data?.pages[0]?.message || "Something went wrong"} />, { variant: "error" });
        });
    }, []);
    useEffect(() => {
        fetchData();
    }, [pageNo, pageSize, searchTerm]);
    const handleChangePage = (pageNo: number) => {
        setPageNo(pageNo + 1);
    }
    const handleChangeRows = (pageSize: number) => {
        setPageSize(pageSize);
        setPageNo(1);
    }
    const onEdit = (row: any) => navigate(`/inspection-section/${params?.id}/edit/` + row.id);
    const showRowView = (row: any) => navigate(`/inspection-section/${params?.id}/edit/` + row.id);
    const onDelete = (row: any) => {
        setDeleteModal(true);
        setRow(row);
    }
    const clearSearch = () => {
        setSearch('');
        setSearchTerm('');
        if (searchTerm) { handleSearch(''); }
    }
    const handleSearch = (search: string) => {
        setSearchTerm(search);
        setPageNo(1);
        setPageSize(pageSize);
    };
    const handleSearchClick = () => {
        const trimmedSearch = search.trim();
        if (trimmedSearch !== '') {
            handleSearch(trimmedSearch);
        }
    };
    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearch(event.target.value.trim());
        if (!event.target.value.trim()) handleSearch('');
    };
    const onChangeStatus = (row: any) => {
        setRow(row);
        setStatusModal(true);
    }
    const actionslist = [
        { label: "edit", onClick: onEdit, icon: <EditIcon /> },
        { label: "delete", onClick: onDelete, icon: <DeleteIcon /> },
    ];
    const adjustActions = (actions: any[]) => {
        return checkPrivileges('ROLE_INSPECTIONFORM_SECTION_UPDATE') 
          ? (checkPrivileges('ROLE_INSPECTIONFORM_SECTION_ARCHIVE') 
              ? actions 
              : actions.filter(action => action.label !== 'delete')) 
          : (checkPrivileges('ROLE_INSPECTIONFORM_SECTION_ARCHIVE') 
              ? actions.filter(action => action.label !== 'edit') 
              : []);
    }

    return (
        <Box>
            <ComponentHeaderTwo
                showBtn={true}
                showSaveBtn={checkPrivileges("ROLE_INSPECTIONFORM_SECTION_CREATE")}
                onClick={() => navigate(`/inspection-forms`)}
                onSubmit={() => navigate(`/inspection-section/${params?.id}/create`)}
                heading={t('inspectionForms')}
                icon={<Inspection height={28} width={28} />}
                btnText={"back"}
                btnType={"back"}
                showCreateBtn={false}
                saveBtnText={"create"}
                isBtnEnabled={true}
                breadcrumbItems={breadcrumbItems}
            />
            <Box className={`flex`}>
                <InspectionFormTabs params={params} selectedIndex={1} />
                <div className='w-[100%] mt-[10px]'>
                    {/* <ComponentSubheader
                        showBtn={true}
                        showSaveBtn={false}
                        onClick={() => navigate(`/inspection-section/${params?.id}/create`)}
                        heading={t('inspectionSections')}
                        icon={<Inspection height={28} width={28} />}
                        btnText={"create"}
                        btnType={"create"}
                        showCreateBtn={checkPrivileges("ROLE_INSPECTIONFORM_SECTION_CREATE")}
                    /> */}
                    {(isFetching || loading) ? <Loader /> :
                        <div id="mainListingTable">
                            <BackendPaginationTable
                                columns={[
                                    { id: 'title', label: 'title', numeric: false },
                                    ...(checkPrivileges("ROLE_INSPECTIONFORM_SECTION_ACTIVE") ? [{ id: 'active', label: 'status', numeric: false }] : []),
                                    ...(adjustActions(actionslist).length ? [{ id: 'actions', label: 'actions', numeric: false }] : []),
                                ]}
                                data={tableData?.pages[0]?.data?.content || []}
                                pageNo={pageNo}
                                pageSize={pageSize}
                                tableData={tableData?.pages[0]?.data || {}}
                                showCheckbox={false}
                                showActions={true}
                                enableSorting={true}
                                showHeader={true}
                                showSearch={true}
                                showFilter={true}
                                showCreateButton={true}
                                actions={adjustActions(actionslist)}
                                handleChangePage={handleChangePage}
                                handleChangeRows={handleChangeRows}
                                onChangeStatus={onChangeStatus}
                                rowClick={checkPrivileges("ROLE_INSPECTIONFORM_SECTION_UPDATE")}
                                showRowView={showRowView}
                                loadingSearch={false}
                                headerComponent={<Grid container spacing={2}>
                                    <Grid item xs={12} md={7} lg={5}>
                                        <StyledInput
                                            fullWidth
                                            placeholder={t("title")}
                                            handleSearchClick={handleSearchClick}
                                            handleChange={handleInputChange}
                                            value={search}
                                            clearSearch={clearSearch}
                                        />
                                    </Grid>
                                </Grid>}
                            />
                        </div>
                    }
                </div>
            </Box>
            {openDeleteModal &&
                <FormDialog
                    open={openDeleteModal}
                    onClose={() => setDeleteModal(false)}
                    title={t('delete') + ' ' + t('InspectionSection')}
                    // icon={<div><Home/></div>}
                    content={<Grid container>
                        <Grid item xs={12}>
                            <Typography>{t("Are you sure you want to delete?")}</Typography>
                        </Grid>
                    </Grid>}
                    saveFunction={() => {
                        setLoading(true);
                        setDeleteModal(false);
                        InspectionSectionProfileService.deleteInspectionSection((row as any).id).then((response: DeleteData) => {
                            if (response.success) {
                                if (tableData?.pages[0]?.data?.content?.length === 1 && (pageNo > 1)) setPageNo(1);
                                else refetch();
                                enqueueSnackbar(<AlertMessages statusCode={response.status} text={t('InspectionSection') + ' ' + t('deleted') + ' ' + t('successfully')} />, { variant: 'success' });
                            } else {
                                enqueueSnackbar(<AlertMessages statusCode={response.status} />, { variant: 'error' });
                            }
                        }).catch((err: any) => {
                            console.error("===", err);
                            enqueueSnackbar(<AlertMessages text={err?.data?.message || err || "Something went wrong"} />, { variant: 'error' });
                        }).finally(() => setLoading(false));
                    }}
                    submitButtonText="Delete"
                />}
            {statusModal &&
                <FormDialog
                    open={statusModal}
                    onClose={() => setStatusModal(false)}
                    title="Change Status"
                    // icon={<div><Home/></div>}
                    content={
                        <Grid container >
                            <Grid item xs={12}  >
                                <Typography>{t(`Are you sure?`)}</Typography>
                            </Grid>
                        </Grid>
                    }
                    saveFunction={() => {
                        setStatusModal(false);
                        setLoading(true);
                        InspectionSectionProfileService.ChangeStatusInspectionSection((row as any).id, row?.active ? "inactive" : "active", {}).then((response: ChangeStatusInspectionSectionResponse) => {
                            if (response.success) {
                                refetch();
                                enqueueSnackbar(<AlertMessages statusCode={response.status} text={t('status') + ' ' + t('changed') + ' ' + t('successfully')} />, { variant: 'success' });
                            } else {
                                enqueueSnackbar(<AlertMessages statusCode={response.status} />, { variant: 'error' });
                            }
                        }).catch((err: any) => {
                            console.error("===", err);
                            enqueueSnackbar(<AlertMessages text={err?.data?.message || err || "Something went wrong"} />, { variant: 'error' });
                        }).finally(() => setLoading(false));
                    }}
                    submitButtonText="OK"
                />
            }
        </Box>
    )
}